import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, ExternalLink, Briefcase, Eye, Trash2 } from "lucide-react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useJobs } from "@/hooks/useJobs";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";

interface RoofMeasurement {
  id: string;
  job_id: string | null;
  address: string;
  total_area: number;
  total_squares: number;
  ridges: number;
  hips: number;
  valleys: number;
  rakes: number;
  eaves: number;
  waste_factor: number;
  created_at: string;
}

interface SidingMeasurement {
  id: string;
  job_id: string | null;
  address: string;
  siding_sqft: number;
  soffit_sqft: number;
  fascia_ft: number;
  material_type: string | null;
  created_at: string;
}

interface GutterMeasurement {
  id: string;
  job_id: string | null;
  address: string;
  total_gutter_length: number;
  downspouts: number;
  size: string | null;
  guard_length: number;
  created_at: string;
}

export function MeasurementHistory({ onAddressClick }: { onAddressClick?: (address: string, type: 'roof' | 'siding' | 'gutters') => void }) {
  const [loading, setLoading] = useState(true);
  const [roofMeasurements, setRoofMeasurements] = useState<RoofMeasurement[]>([]);
  const [sidingMeasurements, setSidingMeasurements] = useState<SidingMeasurement[]>([]);
  const [gutterMeasurements, setGutterMeasurements] = useState<GutterMeasurement[]>([]);
  const [selectedMeasurement, setSelectedMeasurement] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<{ id: string; type: 'roof' | 'siding' | 'gutters' } | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [attaching, setAttaching] = useState(false);
  const [selectedMeasurements, setSelectedMeasurements] = useState<{ id: string; type: 'roof' | 'siding' | 'gutters' }[]>([]);
  
  const { jobs, loading: jobsLoading } = useJobs();
  const navigate = useNavigate();
  const { user, orgId } = useAuth();

  useEffect(() => {
    if (user && orgId) {
      loadMeasurements();
    }
  }, [user, orgId]);

  const loadMeasurements = async () => {
    if (!user || !orgId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {

      const [roofData, sidingData, gutterData] = await Promise.all([
        supabase
          .from('measurements_roof')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false }),
        supabase
          .from('measurements_siding')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false }),
        supabase
          .from('measurements_gutters')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false }),
      ]);

      setRoofMeasurements(roofData.data || []);
      setSidingMeasurements(sidingData.data || []);
      setGutterMeasurements(gutterData.data || []);
    } catch (error: any) {
      console.error("Error loading measurements:", error);
      toast.error("Failed to load measurement history");
    } finally {
      setLoading(false);
    }
  };

  const handleAttachToJob = (measurementId: string, type: 'roof' | 'siding' | 'gutters') => {
    setPendingAttachment({ id: measurementId, type });
    setSelectedJobId("");
    setJobDialogOpen(true);
  };

  const toggleSelected = (id: string, type: 'roof' | 'siding' | 'gutters') => {
    setSelectedMeasurements((current) => {
      const exists = current.find((m) => m.id === id && m.type === type);
      if (exists) {
        return current.filter((m) => !(m.id === id && m.type === type));
      }
      return [...current, { id, type }];
    });
  };

  const clearSelection = () => setSelectedMeasurements([]);

  const handleBulkDelete = async () => {
    if (selectedMeasurements.length === 0) return;
    const confirmDelete = window.confirm(`Delete ${selectedMeasurements.length} measurement(s)? This cannot be undone.`);
    if (!confirmDelete) return;

    try {
      const roofIds = selectedMeasurements.filter((m) => m.type === 'roof').map((m) => m.id);
      const sidingIds = selectedMeasurements.filter((m) => m.type === 'siding').map((m) => m.id);
      const gutterIds = selectedMeasurements.filter((m) => m.type === 'gutters').map((m) => m.id);

      if (roofIds.length) {
        const { error } = await supabase.from('measurements_roof').delete().in('id', roofIds);
        if (error) throw error;
      }
      if (sidingIds.length) {
        const { error } = await supabase.from('measurements_siding').delete().in('id', sidingIds);
        if (error) throw error;
      }
      if (gutterIds.length) {
        const { error } = await supabase.from('measurements_gutters').delete().in('id', gutterIds);
        if (error) throw error;
      }

      toast.success('Selected measurements deleted');
      clearSelection();
      await loadMeasurements();
    } catch (error: any) {
      console.error('Error deleting measurements:', error);
      toast.error('Failed to delete measurements');
    }
  };
  
  const handleConfirmAttachment = async () => {
    if (!selectedJobId || !pendingAttachment) return;
    
    setAttaching(true);
    try {
      const table = pendingAttachment.type === 'roof' 
        ? 'measurements_roof' 
        : pendingAttachment.type === 'siding' 
        ? 'measurements_siding' 
        : 'measurements_gutters';
      
      const { error } = await supabase
        .from(table)
        .update({ job_id: selectedJobId })
        .eq('id', pendingAttachment.id);
      
      if (error) throw error;
      
      toast.success("Measurement attached to job successfully!");
      setJobDialogOpen(false);
      setPendingAttachment(null);
      setSelectedJobId("");
      await loadMeasurements();
    } catch (error: any) {
      console.error("Error attaching measurement:", error);
      toast.error("Failed to attach measurement to job");
    } finally {
      setAttaching(false);
    }
  };

  const handleViewDetails = (measurement: any, type: string) => {
    setSelectedMeasurement({ ...measurement, type });
    setDetailsOpen(true);
  };

  const handleUseForCommercial = (measurement: RoofMeasurement) => {
    // Store in localStorage and redirect
    localStorage.setItem('commercialImport', JSON.stringify({
      totalSqFt: measurement.total_area,
      address: measurement.address,
      measurementId: measurement.id,
    }));
    window.location.href = '/commercial';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const totalMeasurements = roofMeasurements.length + sidingMeasurements.length + gutterMeasurements.length;

  if (totalMeasurements === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Measurement History</CardTitle>
          <CardDescription>No measurements saved yet</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Run a measurement above to save it to history
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Measurement History</CardTitle>
            <CardDescription>
              {totalMeasurements} total measurement{totalMeasurements !== 1 ? 's' : ''} saved
            </CardDescription>
          </div>
          <Button
            variant="destructive"
            size="sm"
            disabled={selectedMeasurements.length === 0}
            onClick={handleBulkDelete}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Selected
          </Button>
        </CardHeader>
        <CardContent>
          {/* Roof Measurements */}
          {roofMeasurements.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                Roof Measurements
                <Badge variant="secondary">{roofMeasurements.length}</Badge>
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      {/* Row selection checkboxes */}
                    </TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Area</TableHead>
                    <TableHead className="text-right">Squares</TableHead>
                    <TableHead className="text-right">Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roofMeasurements.map((measurement) => {
                    const checked = selectedMeasurements.some((m) => m.id === measurement.id && m.type === 'roof');
                    return (
                      <TableRow key={measurement.id}>
                        <TableCell>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleSelected(measurement.id, 'roof')}
                            aria-label="Select roof measurement"
                          />
                        </TableCell>
                        <TableCell 
                          className="font-medium cursor-pointer hover:text-primary hover:underline"
                          onClick={() => {
                            if (onAddressClick) {
                              onAddressClick(measurement.address, 'roof');
                            } else {
                              navigate('/measurements', { state: { address: measurement.address } });
                            }
                          }}
                        >
                          {measurement.address}
                        </TableCell>
                        <TableCell className="text-right">{measurement.total_area?.toLocaleString()} sq ft</TableCell>
                        <TableCell className="text-right">{measurement.total_squares?.toFixed(1)}</TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {format(new Date(measurement.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewDetails(measurement, 'roof')}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleAttachToJob(measurement.id, 'roof')}
                            >
                              <Briefcase className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUseForCommercial(measurement)}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Commercial
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Siding Measurements */}
          {sidingMeasurements.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                Siding Measurements
                <Badge variant="secondary">{sidingMeasurements.length}</Badge>
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Siding</TableHead>
                    <TableHead className="text-right">Soffit</TableHead>
                    <TableHead className="text-right">Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {sidingMeasurements.map((measurement) => {
                  const checked = selectedMeasurements.some((m) => m.id === measurement.id && m.type === 'siding');
                  return (
                    <TableRow key={measurement.id}>
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleSelected(measurement.id, 'siding')}
                          aria-label="Select siding measurement"
                        />
                      </TableCell>
                      <TableCell 
                        className="font-medium cursor-pointer hover:text-primary hover:underline"
                        onClick={() => {
                          if (onAddressClick) {
                            onAddressClick(measurement.address, 'siding');
                          } else {
                            navigate('/measurements', { state: { address: measurement.address } });
                          }
                        }}
                      >
                        {measurement.address}
                      </TableCell>
                      <TableCell className="text-right">{measurement.siding_sqft?.toLocaleString()} sq ft</TableCell>
                      <TableCell className="text-right">{measurement.soffit_sqft?.toLocaleString()} sq ft</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {format(new Date(measurement.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(measurement, 'siding')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleAttachToJob(measurement.id, 'siding')}
                          >
                            <Briefcase className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              </Table>
            </div>
          )}

          {/* Gutter Measurements */}
          {gutterMeasurements.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                Gutter Measurements
                <Badge variant="secondary">{gutterMeasurements.length}</Badge>
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Length</TableHead>
                    <TableHead className="text-right">Downspouts</TableHead>
                    <TableHead className="text-right">Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {gutterMeasurements.map((measurement) => {
                  const checked = selectedMeasurements.some((m) => m.id === measurement.id && m.type === 'gutters');
                  return (
                    <TableRow key={measurement.id}>
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleSelected(measurement.id, 'gutters')}
                          aria-label="Select gutter measurement"
                        />
                      </TableCell>
                      <TableCell 
                        className="font-medium cursor-pointer hover:text-primary hover:underline"
                        onClick={() => {
                          if (onAddressClick) {
                            onAddressClick(measurement.address, 'gutters');
                          } else {
                            navigate('/measurements', { state: { address: measurement.address } });
                          }
                        }}
                      >
                        {measurement.address}
                      </TableCell>
                      <TableCell className="text-right">{measurement.total_gutter_length?.toLocaleString()} ft</TableCell>
                      <TableCell className="text-right">{measurement.downspouts}</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {format(new Date(measurement.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(measurement, 'gutters')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleAttachToJob(measurement.id, 'gutters')}
                          >
                            <Briefcase className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Measurement Details</DialogTitle>
            <DialogDescription>
              {selectedMeasurement?.address}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedMeasurement?.type === 'roof' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Total Area</p>
                  <p className="text-2xl font-bold">{selectedMeasurement.total_area?.toLocaleString()} sq ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total Squares</p>
                  <p className="text-2xl font-bold">{selectedMeasurement.total_squares ? Math.ceil(selectedMeasurement.total_squares) : "-"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Ridges</p>
                  <p className="text-xl">{selectedMeasurement.ridges?.toFixed(1)} ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Hips</p>
                  <p className="text-xl">{selectedMeasurement.hips?.toFixed(1)} ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Valleys</p>
                  <p className="text-xl">{selectedMeasurement.valleys?.toFixed(1)} ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Waste Factor</p>
                  <p className="text-xl">{selectedMeasurement.waste_factor?.toFixed(0)}%</p>
                </div>
              </div>
            )}
            {selectedMeasurement?.type === 'siding' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Siding Area</p>
                  <p className="text-2xl font-bold">{selectedMeasurement.siding_sqft?.toLocaleString()} sq ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Soffit Area</p>
                  <p className="text-2xl font-bold">{selectedMeasurement.soffit_sqft?.toLocaleString()} sq ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Fascia Length</p>
                  <p className="text-xl">{selectedMeasurement.fascia_ft?.toFixed(1)} ft</p>
                </div>
              </div>
            )}
            {selectedMeasurement?.type === 'gutters' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Total Length</p>
                  <p className="text-2xl font-bold">{selectedMeasurement.total_gutter_length?.toLocaleString()} ft</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Downspouts</p>
                  <p className="text-2xl font-bold">{selectedMeasurement.downspouts}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Size</p>
                  <p className="text-xl">{selectedMeasurement.size || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Guard Length</p>
                  <p className="text-xl">{selectedMeasurement.guard_length?.toFixed(1)} ft</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Selection Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach to Job</DialogTitle>
            <DialogDescription>
              Select a job to attach this measurement to
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Select value={selectedJobId} onValueChange={setSelectedJobId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a job" />
              </SelectTrigger>
              <SelectContent>
                {jobsLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">
                    No jobs available
                  </div>
                ) : (
                  jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{job.owner_name || 'Unnamed Job'}</span>
                        <span className="text-xs text-muted-foreground">{job.property_address}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setJobDialogOpen(false)} disabled={attaching}>
              Cancel
            </Button>
            <Button onClick={handleConfirmAttachment} disabled={!selectedJobId || attaching}>
              {attaching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Attaching...
                </>
              ) : (
                "Attach"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
